import React, { useMemo, useEffect } from 'react';
import { formatCurrency } from '../utils/formatCurrency';

const tierThemes = {
  white: {
    cardBackground: 'linear-gradient(120deg, #0d1b3c 0%, #1f3b73 70%, #254287 100%)',
    cardShadow: '0 6px 14px rgba(13, 27, 60, 0.18)',
    progressBase: 'linear-gradient(90deg, #4cd4ff, #73f5cf)',
    progressGlow: '0 0 12px rgba(76, 212, 255, 0.6)',
    projectedFill: 'linear-gradient(90deg, #fddf5a, #ff9836, #ff5c5c)',
    projectedGlow: '0 0 18px rgba(255,143,31,0.4)',
    projectedFilter: 'saturate(1.08)',
    projectedOverlay:
      'linear-gradient(90deg, rgba(253,223,90,0.85), rgba(255,152,54,0.9), rgba(255,92,92,0.85)), repeating-linear-gradient(120deg, rgba(255,255,255,0.85) 0, rgba(255,255,255,0.85) 22px, rgba(255,152,54,0.45) 22px, rgba(255,152,54,0.45) 44px)',
    projectedOverlayOpacity: 0.55,
    projectedOverlayBlend: 'screen',
    cartPillBackground: 'rgba(255,255,255,0.16)',
    cartPillText: '#ffffff',
    multiplierPillBackground: '#ffffff',
    multiplierPillColor: '#0d1b3c',
    pointsLabelColor: 'rgba(255,255,255,0.75)',
  },
  maroon: {
    cardBackground: 'linear-gradient(130deg, #35040f 0%, #5d1422 55%, #7e1f39 100%)',
    cardShadow: '0 6px 16px rgba(87, 10, 36, 0.35)',
    progressBase: 'linear-gradient(90deg, #ff92a5, #ffbfd8)',
    progressGlow: '0 0 14px rgba(255, 155, 175, 0.45)',
    projectedFill: 'linear-gradient(90deg, #ffe07d, #ff9f7c, #ff7085)',
    projectedGlow: '0 0 18px rgba(255,125,150,0.45)',
    projectedFilter: 'saturate(1.15)',
    projectedOverlay:
      'linear-gradient(90deg, rgba(255,224,125,0.85), rgba(255,159,124,0.88), rgba(255,112,133,0.85)), repeating-linear-gradient(120deg, rgba(255,255,255,0.82) 0, rgba(255,255,255,0.82) 20px, rgba(255,153,188,0.45) 20px, rgba(255,153,188,0.45) 40px)',
    projectedOverlayOpacity: 0.6,
    projectedOverlayBlend: 'screen',
    cartPillBackground: 'rgba(255,170,195,0.22)',
    cartPillText: '#ffeef6',
    multiplierPillBackground: '#ffe3ec',
    multiplierPillColor: '#60132a',
    pointsLabelColor: 'rgba(255,220,230,0.78)',
  },
  black: {
    cardBackground: 'linear-gradient(130deg, #050505 0%, #161616 55%, #2c2c2c 100%)',
    cardShadow: '0 6px 18px rgba(0,0,0,0.45)',
    progressBase: 'linear-gradient(90deg, #7c8cff, #a0b2ff)',
    progressGlow: '0 0 14px rgba(160, 178, 255, 0.45)',
    projectedFill: 'linear-gradient(90deg, #6df0ff, #4de0d1, #38c9a8)',
    projectedGlow: '0 0 20px rgba(68,220,212,0.45)',
    projectedFilter: 'saturate(1.1)',
    projectedOverlay:
      'linear-gradient(90deg, rgba(109,240,255,0.8), rgba(77,224,209,0.82), rgba(56,201,168,0.78)), repeating-linear-gradient(120deg, rgba(255,255,255,0.75) 0, rgba(255,255,255,0.75) 20px, rgba(77,224,209,0.4) 20px, rgba(77,224,209,0.4) 40px)',
    projectedOverlayOpacity: 0.5,
    projectedOverlayBlend: 'screen',
    cartPillBackground: 'rgba(255,255,255,0.12)',
    cartPillText: '#cffaf5',
    multiplierPillBackground: '#1ce3d4',
    multiplierPillColor: '#00221f',
    pointsLabelColor: 'rgba(200,228,255,0.76)',
  },
} as const;

interface LoyaltyStatusProps {
  tier: string;
  currentPoints: number;
  nextTierLabel: string | null;
  pointsToNext: number | null;
  cartTotal: number;
  pointsEarned: number;
  effectiveMultiplier: number;
  isGuest?: boolean;
  celebrating?: boolean;
}

export const LoyaltyStatus: React.FC<LoyaltyStatusProps> = ({
  tier,
  currentPoints,
  nextTierLabel,
  pointsToNext,
  cartTotal,
  pointsEarned,
  effectiveMultiplier,
  isGuest = false,
  celebrating = false,
}) => {
  useEffect(() => {
    if (document.getElementById('intan-loyalty-progress-animations')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'intan-loyalty-progress-animations';
    style.textContent = `
      @keyframes intan-loyalty-flow {
        0% {
          background-position: 0 0;
          opacity: 0.25;
        }
        50% {
          opacity: 0.45;
        }
        100% {
          background-position: -200% 0;
          opacity: 0.25;
        }
      }

      @keyframes intan-loyalty-celebrate-pulse {
        0% {
          transform: scale(0.98);
          opacity: 0.55;
        }
        50% {
          transform: scale(1.05);
          opacity: 0.9;
        }
        100% {
          transform: scale(0.98);
          opacity: 0.55;
        }
      }
    `;
    document.head.appendChild(style);
  }, []);

  const { currentProgress, projectedProgress } = useMemo(() => {
    if (isGuest) {
      return {
        currentProgress: 0,
        projectedProgress: 0,
      };
    }

    if (!nextTierLabel || pointsToNext === null) {
      return {
        currentProgress: 1,
        projectedProgress: 1,
      };
    }

    const nextTierThreshold = currentPoints + pointsToNext;
    if (nextTierThreshold <= 0) {
      return {
        currentProgress: 1,
        projectedProgress: 1,
      };
    }

    const projected = Math.min(
      (currentPoints + pointsEarned) / nextTierThreshold,
      1
    );
    return {
      currentProgress: Math.min(currentPoints / nextTierThreshold, 1),
      projectedProgress: projected,
    };
  }, [currentPoints, pointsEarned, pointsToNext, nextTierLabel, isGuest]);

  const formattedPointsEarned = `${Math.max(Math.round(pointsEarned), 0)} pts`;
  const formattedMultiplierLabel = effectiveMultiplier.toFixed(2).replace(/\.00$/, '');
  const themeKey = tier.trim().toLowerCase() as keyof typeof tierThemes;
  const theme = tierThemes[themeKey] ?? tierThemes.white;

  return (
    <div
      style={{
        marginBottom: '14px',
        padding: '14px',
        background: theme.cardBackground,
        color: '#fff',
        borderRadius: '12px',
        boxShadow: theme.cardShadow,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle at top right, rgba(255,255,255,0.15), transparent 55%)',
          pointerEvents: 'none',
        }}
      />
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {isGuest ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}>
              <a
                href="https://familysk.sitikhadijah.com/home"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '1.8px',
                  opacity: 0.7,
                  color: '#fff',
                  textDecoration: 'none',
                }}
              >
                SK Rewards
              </a>
              <div style={{ fontSize: '18px', fontWeight: 700, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                Earn {formattedPointsEarned} when you join
              </div>
              <div
                style={{
                  fontSize: '11px',
                  backgroundColor: theme.cartPillBackground,
                  padding: '4px 10px',
                  borderRadius: '999px',
                  fontWeight: 600,
                  letterSpacing: '0.4px',
                  whiteSpace: 'nowrap',
                  color: theme.cartPillText,
                }}
              >
                {formatCurrency(cartTotal)} cart · ×{formattedMultiplierLabel}
              </div>
            </div>
            <div
              style={{
                fontSize: '12px',
                padding: '6px 10px',
                borderRadius: '8px',
                backgroundColor: 'rgba(255,255,255,0.16)',
                fontWeight: 600,
                letterSpacing: '0.3px',
                color: '#fff',
              }}
            >
              Join now to collect these points
            </div>
          </div>
        ) : (
          <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '2px', opacity: 0.68 }}>
              Loyalty Tier
            </div>
            <div style={{ fontSize: '22px', fontWeight: 700, letterSpacing: '0.8px', lineHeight: 1.2 }}>
              {tier}
            </div>
            <div style={{ fontSize: '11px', opacity: 0.78, marginTop: '4px', letterSpacing: '0.3px' }}>
              {nextTierLabel && pointsToNext !== null
                ? `${Math.max(pointsToNext, 0)} pts to reach ${nextTierLabel}`
                : 'Legend status unlocked'}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1.5px', color: theme.pointsLabelColor }}>
                  Points Earned
                </div>
                <div style={{ fontSize: '22px', fontWeight: 700, letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                  {formattedPointsEarned}
                </div>
              </div>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: theme.multiplierPillBackground,
                  color: theme.multiplierPillColor,
                  fontWeight: 700,
                  padding: '4px 10px',
                  borderRadius: '999px',
                  fontSize: '11px',
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  boxShadow: '0 3px 8px rgba(0,0,0,0.2)',
                }}
              >
                ×{formattedMultiplierLabel}
              </span>
            </div>
            <div
              style={{
                fontSize: '11px',
                backgroundColor: theme.cartPillBackground,
                padding: '4px 10px',
                borderRadius: '999px',
                fontWeight: 600,
                letterSpacing: '0.4px',
                whiteSpace: 'nowrap',
                color: theme.cartPillText,
              }}
            >
              {formatCurrency(cartTotal)} cart
            </div>
          </div>
        </div>

            <div
              style={{
                position: 'relative',
                height: '12px',
                borderRadius: '999px',
                overflow: 'hidden',
                backgroundColor: 'rgba(255,255,255,0.1)',
                boxShadow: celebrating
                  ? '0 0 20px rgba(255, 255, 255, 0.55), 0 0 35px rgba(255, 215, 0, 0.45)'
                  : 'none',
                transition: 'box-shadow 0.5s ease',
              }}
            >
              {celebrating && (
                <div
                  style={{
                    position: 'absolute',
                    inset: '-6px',
                    borderRadius: '999px',
                    background:
                      'radial-gradient(circle at center, rgba(255,255,255,0.55), rgba(255,215,0,0.35) 40%, rgba(255,215,0,0.05) 70%)',
                    filter: 'blur(1px)',
                    animation: 'intan-loyalty-celebrate-pulse 1.8s ease-in-out infinite',
                    pointerEvents: 'none',
                  }}
                />
              )}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: `${currentProgress * 100}%`,
              background: theme.progressBase,
              transition: 'width 0.8s ease',
                  boxShadow: celebrating
                    ? '0 0 25px rgba(255, 255, 255, 0.6), 0 0 50px rgba(255, 215, 0, 0.6)'
                    : theme.progressGlow,
              borderRadius: '999px 0 0 999px',
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: `${currentProgress * 100}%`,
              height: '100%',
              width: `${Math.max(projectedProgress - currentProgress, 0) * 100}%`,
              backgroundImage: theme.projectedFill,
              borderRadius: Math.max(projectedProgress - currentProgress, 0) > 0 ? '0 999px 999px 0' : '0',
                  boxShadow: celebrating
                    ? '0 0 20px rgba(255,255,255,0.6), 0 0 45px rgba(255, 215, 0, 0.7)'
                    : theme.projectedGlow,
              filter: theme.projectedFilter,
              transition: 'width 0.9s ease, left 0.9s ease',
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: `${currentProgress * 100}%`,
              height: '100%',
              width: `${Math.max(projectedProgress - currentProgress, 0) * 100}%`,
              backgroundImage: theme.projectedOverlay,
              backgroundSize: '200% 100%',
              animation: 'intan-loyalty-flow 6.5s linear infinite',
              opacity: theme.projectedOverlayOpacity,
              transition: 'width 0.9s ease, left 0.9s ease',
              borderRadius: Math.max(projectedProgress - currentProgress, 0) > 0 ? '0 999px 999px 0' : '0',
              backgroundBlendMode: theme.projectedOverlayBlend,
            }}
          />
        </div>
          </>
        )}
      </div>
    </div>
  );
};


